/**
 * Real-time availability synchronization hook
 * Uses WebSocket to receive live updates when availability changes
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io, Socket } from 'socket.io-client';
import { getStoredSlug } from '@/lib/utils/localStorage';
import { availabilityKeys } from '@/lib/query-keys';

interface AvailabilityChangeEvent {
  userId: number;
  date: string;
  mealType: 'lunch' | 'dinner';
  isPresent: boolean;
  changedBy: string;
}

interface BulkAvailabilityChangeEvent {
  userId: number;
  changes: Array<{
    date: string;
    mealType: 'lunch' | 'dinner';
    isPresent: boolean;
  }>;
  changedBy: string;
}

// Get WebSocket URL from API base URL
function getWebSocketUrl(): string {
  const apiBaseUrl =
    import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
  // Remove /api suffix and use /availability namespace
  const baseUrl = apiBaseUrl.replace(/\/api$/, '');
  return `${baseUrl}/availability`;
}

/**
 * Hook to synchronize availability data in real-time
 *
 * Features:
 * - Connects to WebSocket server on mount
 * - Listens for availability changes
 * - Invalidates TanStack Query cache when changes occur
 * - Automatically reconnects on disconnect
 * - Syncs across all tabs/browsers of the same user
 *
 * @example
 * ```tsx
 * function AvailabilityPage() {
 *   useAvailabilitySync();
 *   const { data } = useAvailability({ ... });
 *   // data will be automatically refreshed when any change occurs
 * }
 * ```
 */
export function useAvailabilitySync() {
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const userSlug = getStoredSlug();

  const handleAvailabilityChange = useCallback(
    (_event: AvailabilityChangeEvent) => {
      // Always invalidate - ensures sync across all tabs/browsers
      queryClient.invalidateQueries({
        queryKey: availabilityKeys.all,
      });
    },
    [queryClient]
  );

  const handleBulkAvailabilityChange = useCallback(
    (_event: BulkAvailabilityChangeEvent) => {
      // Always invalidate - ensures sync across all tabs/browsers
      queryClient.invalidateQueries({
        queryKey: availabilityKeys.all,
      });
    },
    [queryClient]
  );

  useEffect(() => {
    if (!userSlug) {
      return;
    }

    const wsUrl = getWebSocketUrl();
    const socket = io(wsUrl, {
      // Authentication: pass userSlug at connection time
      auth: { userSlug },
      query: { userSlug },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setIsConnected(true);
    });

    socket.on('availability:changed', handleAvailabilityChange);
    socket.on('availability:bulk-changed', handleBulkAvailabilityChange);

    socket.on('error', (error: { message: string }) => {
      console.error('[AvailabilitySync] Server error:', error.message);
    });

    socket.on('disconnect', (reason) => {
      console.debug('[AvailabilitySync] Disconnected:', reason);
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.debug('[AvailabilitySync] Connection error:', error.message);
    });

    return () => {
      socket.off('availability:changed', handleAvailabilityChange);
      socket.off('availability:bulk-changed', handleBulkAvailabilityChange);
      socket.off('error');
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [userSlug, handleAvailabilityChange, handleBulkAvailabilityChange]);

  return { isConnected };
}
